import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScaleIcon, ClipboardListIcon, ArrowLeftIcon } from "lucide-react";
import { toast } from "sonner";
import { fmtKg } from "./IndicadorBalanca";
import { PesoInput, rotulos } from "./PesagemModal";

const inputCls = "bg-[#0A0A0C] border-border rounded-none h-10 text-sm";
export const OPERACOES_AVULSA = [
  { v: "CARGA", t: "Carga (entra vazio, sai cheio)" },
  { v: "DESCARGA", t: "Descarga (entra cheio, sai vazio)" },
  { v: "OUTROS", t: "Outros / Transferência (só entrada e saída)" },
];
const FORM_VAZIO = { tipo_operacao: "CARGA", placa_cavalo: "", placa_carreta: "", motorista: "", transportadora: "", nota_fiscal: "", produto: "", observacoes: "" };

const Etapa = ({ n, ativa, feita, txt }) => (
  <div className={`flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold ${ativa ? "text-[#FFD60A]" : feita ? "text-[#34C759]" : "text-muted-foreground"}`}>
    <span className={`w-5 h-5 border flex items-center justify-center text-[10px] ${ativa ? "border-[#FFD60A] bg-[#FFD60A] text-black" : feita ? "border-[#34C759]" : "border-border"}`}>{n}</span>{txt}
  </div>
);

/**
 * Pesagem AVULSA (veículo sem entrada na Portaria): 1) Dados da operação → 2) Pesagem inicial (abre o ticket).
 * A pesagem final é feita pelo botão "2ª Pesagem" do ticket aberto (tabela de tickets) e abre a impressão.
 */
export function PesagemAvulsaModal({ open, onOpenChange, serial, onSaved }) {
  const [etapa, setEtapa] = useState(1);
  const [form, setForm] = useState(FORM_VAZIO);
  const [peso, setPeso] = useState("");
  const [origem, setOrigem] = useState("MANUAL");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "observacoes" ? e.target.value : e.target.value.toUpperCase() }));
  const r = rotulos(form.tipo_operacao === "OUTROS" ? "MISTA" : form.tipo_operacao);

  useEffect(() => { if (open) { setEtapa(1); setForm(FORM_VAZIO); setPeso(""); setOrigem("MANUAL"); } }, [open]);

  const iniciar = () => {
    if (form.placa_cavalo.replace(/[^A-Z0-9]/g, "").length < 6) return toast.error("Informe a placa do veículo");
    setEtapa(2);
  };
  const capturar = () => {
    if (serial.peso == null) return toast.error("Sem leitura do indicador — conecte a porta ou digite o peso");
    if (!serial.estavel) toast.warning("Leitura ainda não está estável — confira o valor capturado");
    setPeso(String(Math.round(serial.peso))); setOrigem("SERIAL");
  };
  const salvar = async () => {
    const kg = parseFloat(String(peso).replace(",", "."));
    if (!kg || kg <= 0) return toast.error("Informe o peso em kg");
    setSaving(true);
    try {
      const { data } = await api.post("/balanca/pesagens/avulsa", {
        ...form, placa_cavalo: form.placa_cavalo.replace(/[^A-Z0-9]/g, ""), placa_carreta: form.placa_carreta.replace(/[^A-Z0-9]/g, "") || null,
        motorista: form.motorista || null, transportadora: form.transportadora || null, nota_fiscal: form.nota_fiscal || null,
        produto: form.produto || null, observacoes: form.observacoes || null, peso: kg, origem,
      });
      toast.success(`Ticket ${data.ticket_str} aberto (AVULSA) · ${fmtKg(data.peso1)} — faça a 2ª pesagem na saída`);
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14151A] border-border rounded-none max-w-2xl" data-testid="modal-pesagem-avulsa">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2">
            <ScaleIcon className="w-5 h-5 text-[#FFD60A]" /> Pesagem avulsa · sem entrada na Portaria
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-4 flex-wrap border-b border-border pb-3" data-testid="avulsa-etapas">
          <Etapa n={1} ativa={etapa === 1} feita={etapa > 1} txt="Dados da operação" />
          <Etapa n={2} ativa={etapa === 2} txt="Pesagem inicial" />
          <Etapa n={3} txt="Pesagem final (na saída, pelo ticket)" />
        </div>

        {etapa === 1 ? (
          <div className="space-y-3" data-testid="avulsa-dados">
            <div className="text-[10px] uppercase tracking-widest text-[#FFD60A] flex items-center gap-2"><ClipboardListIcon className="w-3.5 h-3.5" /> Dados da operação</div>
            <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Operação</Label>
              <select value={form.tipo_operacao} onChange={set("tipo_operacao")} className={`${inputCls} w-full px-2 border`} data-testid="avulsa-operacao">
                {OPERACOES_AVULSA.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
              </select></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Placa (cavalo) *</Label>
                <Input value={form.placa_cavalo} onChange={set("placa_cavalo")} maxLength={8} placeholder="ABC1D23" className={`${inputCls} font-mono`} data-testid="avulsa-placa" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Placa carreta</Label>
                <Input value={form.placa_carreta} onChange={set("placa_carreta")} maxLength={8} placeholder="opcional" className={`${inputCls} font-mono`} data-testid="avulsa-placa-carreta" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Nota fiscal</Label>
                <Input value={form.nota_fiscal} onChange={set("nota_fiscal")} placeholder="Nº da NF" className={`${inputCls} font-mono`} data-testid="avulsa-nf" /></div>
            </div>
            <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Motorista</Label>
              <Input value={form.motorista} onChange={set("motorista")} className={inputCls} data-testid="avulsa-motorista" /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Transportadora</Label>
                <Input value={form.transportadora} onChange={set("transportadora")} className={inputCls} data-testid="avulsa-transportadora" /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Produto</Label>
                <Input value={form.produto} onChange={set("produto")} className={inputCls} data-testid="avulsa-produto" /></div>
            </div>
            <div><Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Observações</Label>
              <Textarea value={form.observacoes} onChange={set("observacoes")} rows={2} className="bg-[#0A0A0C] border-border rounded-none text-sm" placeholder="Lacre, avarias, umidade..." data-testid="avulsa-obs" /></div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="avulsa-pesagem">
            <div className="border border-border bg-[#0A0A0C] p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="avulsa-resumo">
              <div><div className="text-[9px] uppercase text-muted-foreground">Placas</div><div className="font-mono font-bold text-[#FFD60A]">{form.placa_cavalo}{form.placa_carreta ? ` / ${form.placa_carreta}` : ""}</div></div>
              <div><div className="text-[9px] uppercase text-muted-foreground">Motorista</div><div className="font-semibold text-white truncate">{form.motorista || "—"}</div></div>
              <div><div className="text-[9px] uppercase text-muted-foreground">Transportadora</div><div className="truncate">{form.transportadora || "—"}</div></div>
              <div><div className="text-[9px] uppercase text-muted-foreground">Operação</div><div className="font-bold text-[#00E5FF]">{form.tipo_operacao}{form.nota_fiscal ? ` · NF ${form.nota_fiscal}` : ""}</div></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="border border-[#FFD60A]/50 bg-[#FFD60A]/5 p-3">
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{r.p1}</div>
                <PesoInput peso={peso} setPeso={(v) => { setPeso(v); setOrigem("MANUAL"); }} origem={origem} serial={serial} capturar={capturar} testid="avulsa-peso" />
              </div>
              <div className="border border-border opacity-50 p-3">
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{r.p2}</div>
                <div className="font-mono text-2xl font-black text-muted-foreground mt-1">— kg</div>
                <div className="text-[10px] text-muted-foreground mt-1">Feita na saída pelo botão <b>2ª Pesagem</b> do ticket; o ticket de impressão abre em seguida.</div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {etapa === 2 && <Button variant="outline" onClick={() => setEtapa(1)} className="rounded-none mr-auto" data-testid="avulsa-voltar"><ArrowLeftIcon className="w-4 h-4 mr-1" /> Voltar</Button>}
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none" data-testid="avulsa-cancelar">Cancelar</Button>
          {etapa === 1
            ? <Button onClick={iniciar} data-testid="avulsa-iniciar" className="rounded-none font-bold uppercase tracking-wider text-black bg-[#FFD60A] hover:bg-[#FFD60A]/85">Iniciar pesagem</Button>
            : <Button onClick={salvar} disabled={saving} data-testid="avulsa-salvar" className="rounded-none font-bold uppercase tracking-wider text-black bg-[#FFD60A] hover:bg-[#FFD60A]/85">{saving ? "Salvando..." : "Salvar 1ª pesagem · abrir ticket"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
